import React from 'react';
import { Quote, TrendingUp, BookOpen, ListChecks, Sparkles } from 'lucide-react';

/**
 * Phase 8 — AI Citation Cards
 *
 * Extractable, LLM-optimized content modules with typed variants:
 *   - fact       : one-line assertion + citation
 *   - stat       : big number + label + source
 *   - definition : term + description (schema-friendly)
 *   - checklist  : titled list of items
 *
 * All variants ship:
 *   - data-ai-citation="<variant>"
 *   - data-speakable="true"
 *   - itemProp="text" (for schema.org CreativeWork nesting)
 *   - unique data-testid per variant
 */

const VARIANT_STYLES = {
  fact: {
    ring: 'border-pink-200',
    bg: 'bg-gradient-to-br from-pink-50 via-white to-rose-50',
    pill: 'bg-pink-100 text-pink-700',
    icon: Quote,
    label: 'Fact',
  },
  stat: {
    ring: 'border-blue-200',
    bg: 'bg-gradient-to-br from-blue-50 via-white to-indigo-50',
    pill: 'bg-blue-100 text-blue-700',
    icon: TrendingUp,
    label: 'Statistic',
  },
  definition: {
    ring: 'border-slate-200',
    bg: 'bg-slate-50',
    pill: 'bg-slate-200 text-slate-700',
    icon: BookOpen,
    label: 'Definition',
  },
  checklist: {
    ring: 'border-emerald-200',
    bg: 'bg-gradient-to-br from-emerald-50 via-white to-emerald-50/50',
    pill: 'bg-emerald-100 text-emerald-700',
    icon: ListChecks,
    label: 'Checklist',
  },
};

export const AICitationCard = ({
  variant = 'fact',
  title,
  body,
  value,        // for stat variant
  unit,         // for stat variant
  items = [],   // for checklist variant
  source,       // citation source
  compact = false,
  className = '',
}) => {
  const v = VARIANT_STYLES[variant] || VARIANT_STYLES.fact;
  const Icon = v.icon;

  return (
    <aside
      className={`rounded-2xl border-2 ${v.ring} ${v.bg} ${compact ? 'p-4' : 'p-5 md:p-6'} ${className}`}
      data-ai-citation={variant}
      data-testid={`ai-citation-${variant}`}
      data-speakable="true"
      itemScope
      itemType="https://schema.org/CreativeWork"
    >
      <div className={`inline-flex items-center gap-1.5 ${v.pill} px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider mb-2`}>
        <Icon className="w-3 h-3" />
        <span>{v.label}</span>
      </div>

      {variant === 'stat' && (
        <div className="flex items-baseline gap-2 mb-1">
          <div className="text-3xl md:text-4xl font-bold text-slate-900" itemProp="value" data-testid="citation-stat-value">
            {value}
          </div>
          {unit && <div className="text-sm text-slate-500 font-medium">{unit}</div>}
        </div>
      )}

      {title && (
        <h3 className={`${variant === 'stat' ? 'text-base' : 'text-lg'} font-bold text-slate-900 mb-1`} itemProp="headline">
          {title}
        </h3>
      )}

      {body && (
        <p className="text-sm md:text-base text-slate-700 leading-relaxed" itemProp="text">
          {body}
        </p>
      )}

      {variant === 'checklist' && items.length > 0 && (
        <ul className="mt-2 space-y-1.5" data-testid="citation-checklist-items">
          {items.map((it, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
              <span className="text-emerald-500 mt-0.5">✓</span>
              <span>{it}</span>
            </li>
          ))}
        </ul>
      )}

      {source && (
        <div className="mt-3 pt-2 border-t border-slate-200/70 text-[11px] text-slate-500">
          <strong className="text-slate-600">Source:</strong> {source}
        </div>
      )}
    </aside>
  );
};

/**
 * AICitationGrid — renders 3-6 citation cards in a responsive grid.
 */
export const AICitationGrid = ({ items = [], title, className = '' }) => {
  if (!items.length) return null;
  return (
    <section className={`my-8 ${className}`} data-testid="ai-citation-grid">
      {title && (
        <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-pink-500" />
          {title}
        </h2>
      )}
      <div className={`grid grid-cols-1 md:grid-cols-${Math.min(items.length, 3)} gap-3`}>
        {items.map((it, i) => (
          <AICitationCard key={i} {...it} compact />
        ))}
      </div>
    </section>
  );
};

export default AICitationCard;
